/**
 * Example prompts for the Voice Design feature, lifted from ElevenLabs'
 * official prompting guide:
 * https://elevenlabs.io/docs/eleven-creative/voices/voice-design#prompting-guide
 *
 * Clicking an example fills the description field so users can see what a good
 * prompt looks like, then tweak it.
 */
export interface VoiceDesignExample {
  emoji: string;
  name: string;
  prompt: string;
}

export const VOICE_DESIGN_EXAMPLES: VoiceDesignExample[] = [
  // Professional / commercial use cases first — these reflect what paying
  // customers actually create (video voiceover, audiobooks, ads, e-learning…).
  {
    emoji: '📖',
    name: 'Audiobook Narrator',
    prompt:
      'A warm, engaging male narrator in his 40s with a neutral British accent, rich and measured with subtle emotional depth. He reads at a calm, unhurried pace that draws the listener into the story. Studio-quality recording.',
  },
  {
    emoji: '📚',
    name: 'Explainer Video',
    prompt:
      'A confident, approachable female narrator in her early 30s with a neutral American accent, articulate and reassuring with a knowledgeable tone. She speaks at a measured, easy-to-follow pace, emphasizing key points clearly. Excellent audio quality.',
  },
  {
    emoji: '▶️',
    name: 'YouTube Narrator',
    prompt:
      'A warm, engaging male narrator in his 30s with a neutral American accent, clear and conversational with an upbeat, friendly energy. He speaks at a steady, natural pace that keeps viewers hooked. Studio-quality recording.',
  },
  {
    emoji: '🎧',
    name: 'Podcast Host',
    prompt:
      'A friendly, charismatic female podcast host in her early 30s with a neutral American accent, conversational and upbeat with natural warmth. She speaks at a relaxed, expressive pace, as if chatting with a close friend. Excellent audio quality.',
  },
  {
    emoji: '📣',
    name: 'Commercial Ad',
    prompt:
      'A bright, persuasive female voice in her late 20s with a neutral American accent, warm and confident with an inviting, upbeat delivery. She speaks at a lively pace with polished commercial-spot energy that drives action. Broadcast quality.',
  },
  {
    emoji: '🎞️',
    name: 'Documentary Narrator',
    prompt:
      'A composed, authoritative male narrator in his 50s with a refined British accent, deep and contemplative with a sense of gravitas. He delivers at a deliberate, thoughtful pace that lends weight to every fact. Broadcast quality.',
  },
  {
    emoji: '🏢',
    name: 'E-learning Course',
    prompt:
      'A confident, articulate male narrator in his 40s with a neutral American accent, professional and reassuring with a knowledgeable tone. He speaks at a steady, deliberate pace ideal for online courses and corporate training modules. Broadcast quality.',
  },
  {
    emoji: '🧘',
    name: 'Meditation Guide',
    prompt:
      'A soothing, gentle female voice in her 30s with a soft neutral American accent, calm, nurturing and reassuring. She speaks slowly with long, peaceful pauses and a near-whisper softness that melts away tension. Studio-quality recording.',
  },
  // Playful / character voices
  {
    emoji: '👹',
    name: 'Evil Ogre',
    prompt:
      "A deep, booming voice of a massive evil ogre with a thick, gravelly, resonant tone that's both menacing and absurdly silly. Speaking at a quick, excited pace with erratic bursts of maniacal energy. Perfect audio quality.",
  },
  {
    emoji: '🐭',
    name: 'Little Mouse',
    prompt: 'A cute little squeaky mouse.',
  },
  {
    emoji: '🤠',
    name: 'Southern Woman',
    prompt:
      'An older woman with a thick Southern accent. She is sweet and sarcastic.',
  },
  {
    emoji: '🪖',
    name: 'Drill Sergeant',
    prompt:
      'An army drill sergeant shouting at his team of soldiers. He sounds angry and is speaking at a fast pace.',
  },
  {
    emoji: '🗽',
    name: 'New Yorker',
    prompt:
      'Deep, gravelly, thick New York accent, tough and world-weary, often cynical.',
  },
  {
    emoji: '🎙️',
    name: 'Sports Commentator',
    prompt:
      'A high-energy female sports commentator with a thick British accent, passionately delivering play-by-play coverage.',
  },
  {
    emoji: '🎬',
    name: 'Movie Trailer',
    prompt:
      'Dramatic voice, used to build anticipation in movie trailers, typically associated with action or thrillers.',
  },
  {
    emoji: '🏴‍☠️',
    name: 'Angry Pirate',
    prompt: 'An angry old pirate, loud and boisterous.',
  },
  {
    emoji: '🧪',
    name: 'Mad Scientist',
    prompt:
      'The voice of an eccentric scientific genius with rapid, erratic speech patterns and a German-tinged accent.',
  },
  {
    emoji: '🇬🇧',
    name: 'British Entrepreneur',
    prompt:
      "Excellent audio quality. A man in his 30s to early 40s with a thick British accent, speaking at a natural pace like he's talking to a friend.",
  },
  {
    emoji: '🇸🇬',
    name: 'Singaporean',
    prompt:
      'A friendly Singaporean woman in her early 30s with a natural Singlish lilt — warm, expressive and conversational, speaking at a relaxed, everyday pace. Excellent audio quality.',
  },
];
